import { useEffect, useMemo, useRef, useState } from "react";
import { fitLabelText, layoutPlayerLabels } from "../lib/playerLabels";
import { zoneOpacity, opponentColor, playerPosAt, pitchScreenX as sx, pitchScreenY as sy } from "../lib/engine";
import { JerseyShape, autoAway, gkColors, defaultJersey } from "./Jersey";
import { GaaBallShape } from "./GaaBall";

const M = 10;
const VBW = 90 + 2 * M;
const VBL = 145 + 2 * M;

function Line({ x1, y1, x2, y2, w = 0.4, dash }) {
  return <line x1={sx(x1)} y1={sy(y1)} x2={sx(x2)} y2={sy(y2)} stroke="rgba(255,255,255,0.55)" strokeWidth={w} strokeDasharray={dash} />;
}

function GoalEnd({ y }) {
  const dir = y === 0 ? 1 : -1;
  const top = (d) => Math.min(sy(y), sy(y + dir * d));
  return (
    <g>
      {/* large rectangle 19m x 13m */}
      <rect x={sx(35.5 + 19)} y={top(13)} width={19} height={13} fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth={0.42} />
      {/* small rectangle 14m x 4.5m */}
      <rect x={sx(38 + 14)} y={top(4.5)} width={14} height={4.5} fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth={0.42} />
      {/* goal posts + net box */}
      <rect x={sx(41.75 + 6.5)} y={Math.min(sy(y), sy(y - dir * 2.2))} width={6.5} height={2.2} fill="rgba(255,255,255,0.12)" stroke="#fff" strokeWidth={0.45} />
    </g>
  );
}

export default function OverheadPitch({ play, frame, selectedId, onSelect, onMovePlayer, onMoveWaypoint, tool, showPaths = true, teamColors, onPitchPoint, compact, dragPreview, stageRange, onArrowDraw, ballDraggable, onMoveBall, homeJersey }) {
  const svgRef = useRef(null);
  const drag = useRef(null);
  const [arrowDraw, setArrowDraw] = useState(null);
  const homeColor = teamColors?.primary || "#0EA5E9";
  const AWAY = opponentColor(homeColor);
  const homeKit = homeJersey && homeJersey.body ? homeJersey : defaultJersey(homeColor);
  const awayKit = autoAway(homeColor);
  const [pitchScale, setPitchScale] = useState(3.5);
  const previousLabels = useRef([]);
  const textContext = useMemo(() => document.createElement("canvas").getContext("2d"), []);

  useEffect(() => {
    const svg = svgRef.current;
    const resize = () => {
      const { width, height } = svg.getBoundingClientRect();
      if (width && height) setPitchScale(Math.min(width / VBW, height / VBL));
    };
    const observer = new ResizeObserver(resize);
    observer.observe(svg);
    resize();
    return () => observer.disconnect();
  }, []);

  const labelFontSize = Math.max(2.8, 11.5 / pitchScale);
  const labels = useMemo(() => {
    if (compact || !frame) return [];
    textContext.font = `600 ${labelFontSize}px Outfit, sans-serif`;
    const obstacles = frame.entities.map((e) => ({ x: sx(e.x) - 5.4, y: sy(e.y) - 5.4, width: 10.8, height: 10.8 }));
    if (frame.ball) obstacles.push({ x: sx(frame.ball.x) - 3, y: sy(frame.ball.y) - 3, width: 6, height: 6 });
    const names = frame.entities.filter((e) => e.team === "A" && e.name?.trim())
      .sort((a, b) => Number(b.id === selectedId) - Number(a.id === selectedId))
      .map((e) => {
        const fitted = fitLabelText(e.name, (text) => textContext.measureText(text).width, 38);
        const height = labelFontSize + 1.8;
        return { id: e.id, number: e.number, name: e.name, ...fitted, width: fitted.width + 2.4, height,
          anchorX: sx(e.x), anchorY: sy(e.y), offset: 6.2 + height / 2 };
      });
    const placed = layoutPlayerLabels(names, obstacles, { x: -M + 1, y: -M + 1, width: VBW - 2, height: VBL - 2 }, 0.8, previousLabels.current);
    previousLabels.current = placed;
    return placed;
  }, [compact, frame, selectedId, labelFontSize, textContext]);

  const toWorld = (e) => {
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    const src = e.touches ? e.touches[0] : e;
    pt.x = src.clientX;
    pt.y = src.clientY;
    const p = pt.matrixTransform(svg.getScreenCTM().inverse());
    return { x: Math.max(0, Math.min(90, 90 - p.x)), y: Math.max(0, Math.min(145, 145 - p.y)) };
  };

  const onDown = (ent) => (e) => {
    if (tool === "arrow") return; // let the svg background start the arrow (can begin on a player)
    e.stopPropagation();
    onSelect?.(ent.id);
    if (tool === "select" || tool === "run" || tool === "move") {
      drag.current = { id: ent.id };
    }
  };
  const onHandleDown = (evId, idx) => (e) => {
    e.stopPropagation();
    drag.current = { handle: { evId, idx } };
  };
  const onRootDown = (e) => {
    if (tool === "arrow") {
      const w = toWorld(e);
      drag.current = { arrow: { x1: w.x, y1: w.y, x2: w.x, y2: w.y } };
      setArrowDraw({ ...drag.current.arrow });
    }
  };
  const onMove = (e) => {
    if (!drag.current) return;
    if (drag.current.arrow) { const w = toWorld(e); drag.current.arrow.x2 = w.x; drag.current.arrow.y2 = w.y; setArrowDraw({ ...drag.current.arrow }); return; }
    if (drag.current.ball) { onMoveBall?.(toWorld(e)); return; }
    const w = toWorld(e);
    if (drag.current.handle) onMoveWaypoint?.(drag.current.handle.evId, drag.current.handle.idx, w);
    else onMovePlayer?.(drag.current.id, w);
  };
  const onUp = () => {
    if (!drag.current) return;
    const d = drag.current;
    drag.current = null;
    if (d.arrow) { setArrowDraw(null); if (Math.hypot(d.arrow.x2 - d.arrow.x1, d.arrow.y2 - d.arrow.y1) > 2.5) onArrowDraw?.(d.arrow); return; }
    if (d.ball) { onMoveBall?.(null, true); return; }
    if (d.handle) onMoveWaypoint?.(null, null, null, true);
    else onMovePlayer?.(null, null, true);
  };

  const events = play?.events || [];
  const startEnts = play?.entities || [];

  return (
    <svg
      ref={svgRef}
      data-testid="overhead-pitch"
      viewBox={`${-M} ${-M} ${VBW} ${VBL}`}
      className="h-full w-full touch-none select-none"
      onPointerDown={onRootDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerLeave={onUp}
      onClick={(e) => { if (onPitchPoint && ["addA", "addB", "zone", "cone", "text", "ball"].includes(tool)) onPitchPoint(toWorld(e)); }}
    >
      <defs>
        <radialGradient id="sheen" cx="35%" cy="28%" r="75%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.5" />
          <stop offset="55%" stopColor="#ffffff" stopOpacity="0.08" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <filter id="pshadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="0.5" stdDeviation="0.7" floodColor="#000000" floodOpacity="0.55" />
        </filter>
        <linearGradient id="grass" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0F4A28" />
          <stop offset="100%" stopColor="#0B3820" />
        </linearGradient>
        <marker id="arrow-run" markerWidth="5" markerHeight="5" refX="3.5" refY="2.5" orient="auto">
          <path d="M0,0 L5,2.5 L0,5 z" fill="#F59E0B" />
        </marker>
        <marker id="arrow-ball" markerWidth="5" markerHeight="5" refX="3.5" refY="2.5" orient="auto">
          <path d="M0,0 L5,2.5 L0,5 z" fill="#38BDF8" />
        </marker>
        <marker id="arrow-opp" markerWidth="5" markerHeight="5" refX="3.5" refY="2.5" orient="auto">
          <path d="M0,0 L5,2.5 L0,5 z" fill="#EF4444" />
        </marker>
        <marker id="arrow-draw" markerWidth="5" markerHeight="5" refX="3.5" refY="2.5" orient="auto">
          <path d="M0,0 L5,2.5 L0,5 z" fill="#00F0FF" />
        </marker>
      </defs>

      {/* turf + stripes */}
      <rect x={-M} y={-M} width={VBW} height={VBL} fill="url(#grass)" />
      {Array.from({ length: 10 }).map((_, i) => (
        <rect key={i} x={-M} y={-M + i * (VBL / 10)} width={VBW} height={VBL / 10} fill={i % 2 ? "#ffffff" : "#000000"} opacity={0.03} />
      ))}

      {/* boundary + official markings */}
      <rect x={sx(90)} y={sy(145)} width={90} height={145} fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth={0.55} />
      <Line x1={0} y1={72.5} x2={90} y2={72.5} w={0.45} dash="2 1.4" />
      {[13, 20, 45].map((d) => (<Line key={"a" + d} x1={0} y1={d} x2={90} y2={d} w={0.42} />))}
      {[13, 20, 45].map((d) => (<Line key={"b" + d} x1={0} y1={145 - d} x2={90} y2={145 - d} w={0.42} />))}
      {/* the "D": 13m semi-circle centred on the 20m line, outside it */}
      <path data-testid="pitch-home-d" d={`M ${sx(32)} ${sy(20)} A 13 13 0 0 0 ${sx(58)} ${sy(20)}`} fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth={0.42} />
      <path data-testid="pitch-away-d" d={`M ${sx(32)} ${sy(125)} A 13 13 0 0 1 ${sx(58)} ${sy(125)}`} fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth={0.42} />
      {/* inner arc on the goal side of the 20m line */}
      <path data-testid="pitch-home-inner-arc" d={`M ${sx(38.5)} ${sy(20)} A 6.5 6.5 0 0 1 ${sx(51.5)} ${sy(20)}`} fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth={0.42} />
      <path data-testid="pitch-away-inner-arc" d={`M ${sx(38.5)} ${sy(125)} A 6.5 6.5 0 0 0 ${sx(51.5)} ${sy(125)}`} fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth={0.42} />
      <GoalEnd y={0} />
      <GoalEnd y={145} />

      {/* movement + ball paths */}
      {showPaths && events.map((ev) => {
        const inStage = !stageRange || (ev.startTime < stageRange.end - 1e-6 && ev.endTime > stageRange.start + 1e-6);
        const op = inStage ? 0.9 : 0.22;
        if (ev.kind === "move") {
          const ent = startEnts.find((e) => e.id === ev.playerId);
          if (!ent) return null;
          const from = playerPosAt(ent, events, ev.startTime);
          const pts = [from, ...(ev.waypoints || [])];
          const d = pts.map((p, i) => `${i ? "L" : "M"} ${sx(p.x)} ${sy(p.y)}`).join(" ");
          const opp = ent.team === "B";
          return <path key={ev.id} d={d} fill="none" stroke={opp ? "#EF4444" : "#F59E0B"} strokeWidth={0.55} strokeDasharray={opp ? "1.5 1.2" : undefined} markerEnd={`url(#arrow-${opp ? "opp" : "run"})`} opacity={op} />;
        }
        if (ev.kind === "ball") {
          const from = startEnts.find((e) => e.id === ev.playerId);
          const to = startEnts.find((e) => e.id === ev.targetPlayerId);
          if (!from || (!to && !ev.target)) return null;
          const fp = playerPosAt(from, events, ev.startTime);
          const tp = ev.target || playerPosAt(to, events, ev.endTime);
          return <path key={ev.id} d={`M ${sx(fp.x)} ${sy(fp.y)} L ${sx(tp.x)} ${sy(tp.y)}`} fill="none" stroke="#38BDF8" strokeWidth={0.5} strokeDasharray="0.8 1.2" markerEnd="url(#arrow-ball)" opacity={op} />;
        }
        return null;
      })}

      {/* live drag preview for a run being drawn */}
      {dragPreview && (() => {
        const ent = frame?.entities.find((e) => e.id === dragPreview.id);
        if (!ent) return null;
        const pts = [{ x: ent.x, y: ent.y }, ...(dragPreview.points || [dragPreview])];
        const d = pts.map((p, i) => `${i ? "L" : "M"} ${sx(p.x)} ${sy(p.y)}`).join(" ");
        return (
          <g style={{ pointerEvents: "none" }} data-testid="run-drag-preview">
            <path d={d} fill="none" stroke="#F59E0B" strokeWidth={0.7} strokeDasharray="1.5 1" strokeLinejoin="round" markerEnd="url(#arrow-run)" />
            <circle cx={sx(dragPreview.x)} cy={sy(dragPreview.y)} r={3.2} fill={ent.team === "A" ? homeColor : AWAY} opacity={0.45} stroke="#F59E0B" strokeWidth={0.4} />
          </g>
        );
      })()}


      {/* annotations */}
      {(play?.annotations || []).map((a) => {
        if (a.type === "zone") {
          const op = zoneOpacity(a, frame?.entities || []);
          return (
            <g key={a.id} style={{ pointerEvents: "none" }}>
              <circle cx={sx(a.x)} cy={sy(a.y)} r={a.r} fill="#2DD4BF" opacity={op} />
              <circle cx={sx(a.x)} cy={sy(a.y)} r={a.r} fill="none" stroke="#2DD4BF" strokeWidth={0.4} strokeDasharray="1.6 1.3" opacity={Math.min(0.9, op + 0.4)} />
              <text x={sx(a.x)} y={sy(a.y) + 0.9} textAnchor="middle" fontSize={2.6} fontWeight="700" fill="#5EEAD4" opacity={Math.min(0.95, op + 0.5)}>SPACE</text>
            </g>
          );
        }
        if (a.type === "cone") {
          return <path key={a.id} d={`M ${sx(a.x)} ${sy(a.y) - 2} L ${sx(a.x) - 1.7} ${sy(a.y) + 1.5} L ${sx(a.x) + 1.7} ${sy(a.y) + 1.5} z`} fill="#F97316" stroke="#7c2d12" strokeWidth={0.3} />;
        }
        if (a.type === "text") {
          return <text key={a.id} x={sx(a.x)} y={sy(a.y)} textAnchor="middle" fontSize={3} fontWeight="700" fill="#00F0FF" style={{ fontFamily: "Outfit, sans-serif" }}>{a.text}</text>;
        }
        if (a.type === "arrow") {
          return <line key={a.id} x1={sx(a.x1)} y1={sy(a.y1)} x2={sx(a.x2)} y2={sy(a.y2)} stroke="#00F0FF" strokeWidth={0.7} strokeLinecap="round" markerEnd="url(#arrow-draw)" />;
        }
        return null;
      })}

      {/* live arrow being drawn */}
      {arrowDraw && (
        <line data-testid="arrow-preview" x1={sx(arrowDraw.x1)} y1={sy(arrowDraw.y1)} x2={sx(arrowDraw.x2)} y2={sy(arrowDraw.y2)} stroke="#00F0FF" strokeWidth={0.7} strokeLinecap="round" strokeDasharray="1.5 1" markerEnd="url(#arrow-draw)" style={{ pointerEvents: "none" }} />
      )}

      {/* Connectors sit below jerseys so they cannot cover shirt numbers. */}
      <g style={{ pointerEvents: "none" }} aria-hidden="true">
        {labels.map((label) => (
          <g key={label.id}>
            <line x1={label.anchorX} y1={label.anchorY} x2={label.lineX} y2={label.lineY} stroke="rgba(4,10,16,0.8)" strokeWidth={0.85} />
            <line data-testid={`home-label-connector-${label.id}`} x1={label.anchorX} y1={label.anchorY} x2={label.lineX} y2={label.lineY}
              stroke={label.id === selectedId ? "#00F0FF" : "rgba(255,255,255,0.85)"} strokeWidth={0.4} />
          </g>
        ))}
      </g>

      {/* players (animated positions) — rendered as recolorable jerseys */}
      {frame?.entities.map((e) => {
        const sel = e.id === selectedId;
        const cx = sx(e.x), cy = sy(e.y);
        const carrier = frame?.ball?.carrierId === e.id;
        const isGK = e.number === 1;
        const kit = isGK ? gkColors(e.team) : (e.team === "A" ? homeKit : awayKit);
        const half = 5.2;
        return (
          <g key={e.id} data-testid={`pitch-player-${e.id}`} aria-label={`${e.team === "A" ? "Home" : "Opposition"} #${e.number}${e.team === "A" && e.name ? ` ${e.name}` : ""}`} onPointerDown={onDown(e)} style={{ cursor: "grab" }}>
            {sel && <circle cx={cx} cy={cy} r={6} fill="none" stroke="#00F0FF" strokeWidth={0.5} opacity={0.9} />}
            {carrier && <circle cx={cx} cy={cy} r={5.6} fill="none" stroke="#FACC15" strokeWidth={0.6} />}
            <ellipse cx={cx} cy={cy + 4.3} rx={3.4} ry={1.1} fill="#000000" opacity={0.3} />
            <circle cx={cx} cy={cy} r={5.4} fill="transparent" />
            <svg x={cx - half} y={cy - half} width={half * 2} height={half * 2} viewBox="0 0 100 100" style={{ overflow: "visible", pointerEvents: "none" }}>
              <JerseyShape style={kit.style} body={kit.body} sleeves={kit.sleeves} trim={kit.trim} accent={kit.accent} number={e.number} uid={e.id} />
            </svg>
          </g>
        );
      })}

      {/* waypoint handles for the selected player's runs (drag to reshape the curve) */}
      {!compact && selectedId && tool === "select" && events.filter((ev) => ev.kind === "move" && ev.playerId === selectedId).map((ev) =>
        (ev.waypoints || []).map((wp, i) => (
          <g key={`${ev.id}-${i}`} data-testid={`waypoint-handle-${ev.id}-${i}`} onPointerDown={onHandleDown(ev.id, i)} style={{ cursor: "move" }}>
            <circle cx={sx(wp.x)} cy={sy(wp.y)} r={2.6} fill="transparent" />
            <circle cx={sx(wp.x)} cy={sy(wp.y)} r={1.3} fill="#0A0D14" stroke="#F59E0B" strokeWidth={0.5} />
          </g>
        ))
      )}

      {/* ball — GAA football */}
      {frame?.ball && (() => {
        const bx = sx(frame.ball.x), by = sy(frame.ball.y);
        const r = 2.0 + (frame.ball.z || 0) * 0.04;
        return (
          <g data-testid="pitch-ball" onPointerDown={ballDraggable ? (e) => { e.stopPropagation(); drag.current = { ball: true }; } : undefined} style={ballDraggable ? { cursor: "grab" } : undefined}>
            <circle cx={bx} cy={by} r={r + 0.6} fill="transparent" />
            <svg x={bx - r} y={by - r} width={r * 2} height={r * 2} viewBox="0 0 100 100" style={{ overflow: "visible", pointerEvents: "none" }}><GaaBallShape /></svg>
            {ballDraggable && <circle cx={bx} cy={by} r={r + 1.8} fill="none" stroke="#FACC15" strokeWidth={0.35} opacity={0.55} />}
            {frame.ball.inFlight && <circle cx={bx} cy={by} r={r + 0.8} fill="none" stroke="#FACC15" strokeWidth={0.4} opacity={0.5} />}
          </g>
        );
      })()}

      <g data-testid="home-team-labels-layer" style={{ pointerEvents: "none" }}>
        {labels.map((label) => (
          <g key={label.id} data-testid={`home-label-${label.id}`} aria-label={`#${label.number} ${label.name}`}>
            <title>{`#${label.number} ${label.name}`}</title>
            <rect data-testid={`home-label-badge-${label.id}`} x={label.x} y={label.y} width={label.width} height={label.height} rx={1.2}
              fill="#0A0D14" fillOpacity={0.94} stroke={label.id === selectedId ? "#00F0FF" : "rgba(255,255,255,0.3)"} strokeWidth={0.3} />
            <text data-testid={`home-label-text-${label.id}`} x={label.x + label.width / 2} y={label.y + label.height / 2} textAnchor="middle" dominantBaseline="central"
              fontSize={labelFontSize} fontWeight={600} fill="#FFFFFF" style={{ fontFamily: "Outfit, sans-serif" }}>{label.text}</text>
          </g>
        ))}
      </g>
    </svg>
  );
}
